import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CONFIRMATION_STATE_KEY, confirmationOutcome } from '../lib/confirmationLink.js';
import { useSession } from '../session/SessionProvider.js';

function Loading(): React.JSX.Element {
  return (
    <div className="flex min-h-full items-center justify-center p-12">
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    </div>
  );
}

/** Sends anonymous visitors to sign in, remembering where they were headed. */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * Operator-only pages.
 *
 * Cosmetic, not a security boundary: every `/api/admin` route checks the role
 * itself and answers 403. This exists so an agency user who types an admin URL
 * gets a sensible page rather than a wall of failed requests.
 */
export function RequireOperator({ children }: { children: ReactNode }): React.JSX.Element {
  const { status, session } = useSession();
  const location = useLocation();

  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  if (session?.user.role !== 'admin') return <Navigate to="/" replace />;

  return <>{children}</>;
}

/** Keeps signed-in users off the sign-in and sign-up pages. */
export function RequireAnonymous({ children }: { children: ReactNode }): React.JSX.Element {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <Loading />;
  if (status === 'authenticated') {
    /*
     * C-124. A confirmation link lands on `/sign-in`, and somebody signed in is
     * sent on from here before that page could say what the link did. So the
     * answer goes with them, and the dashboard says it once.
     */
    const outcome = confirmationOutcome(location.search);
    return (
      <Navigate
        to="/"
        replace
        {...(outcome ? { state: { [CONFIRMATION_STATE_KEY]: outcome } } : {})}
      />
    );
  }
  return <>{children}</>;
}
